// Lightweight GA4 wrapper with DNT respect + UTM capture.
// Loads gtag.js asynchronously after first call to initAnalytics().

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
  }
}

const MEASUREMENT_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined)?.trim();
const UTM_KEY = "metroshine.utm";
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

let initialized = false;
let lastTrackedPath: string | null = null;

const dntActive = (): boolean => {
  if (typeof navigator === "undefined") return false;
  // @ts-ignore
  const dnt = navigator.doNotTrack || (window as any).doNotTrack || (navigator as any).msDoNotTrack;
  return dnt === "1" || dnt === "yes";
};

const isEnabled = (): boolean => !!MEASUREMENT_ID && !dntActive();

export const initAnalytics = (): void => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  captureUTMFromUrl();
  if (!isEnabled()) return;

  // If the inline snippet in index.html already booted gtag, just reuse it.
  if (typeof window.gtag === "function") return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, { send_page_view: false });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);
};

export const track = (event: string, params: Record<string, any> = {}): void => {
  if (!isEnabled() || !window.gtag) return;
  try {
    window.gtag("event", event, params);
  } catch (e) {
    // never break UX
    console.warn("analytics track failed", e);
  }
};

export const trackPageView = (path: string, title?: string): void => {
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  track("page_view", {
    page_path: path,
    page_location: typeof window !== "undefined" ? window.location.href : path,
    page_title: title ?? (typeof document !== "undefined" ? document.title : undefined),
  });
};

// ── UTM capture ──
export const captureUTMFromUrl = (): void => {
  if (typeof window === "undefined") return;
  try {
    const sp = new URLSearchParams(window.location.search);
    const found: Record<string, string> = {};
    UTM_FIELDS.forEach((k) => {
      const v = sp.get(k);
      if (v) found[k] = v.slice(0, 120);
    });
    if (Object.keys(found).length > 0) {
      sessionStorage.setItem(UTM_KEY, JSON.stringify({ ...found, captured_at: new Date().toISOString() }));
    }
  } catch { /* ignore */ }
};

export const getStoredUTM = (): Record<string, string> => {
  if (typeof window === "undefined") return {};
  try {
    const raw = sessionStorage.getItem(UTM_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};
